/**
 * Two-step modal for sending outbound messages through WhatsApp, SMS, Email, or RCS.
 * Step 0: Select channel (integration), enter recipient, pick AI agent.
 * Step 1: Compose the message (template/text/email editor).
 */

import { useRef, useState } from 'react';

import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { BrainVersionSelector } from '@/components/atoms/BrainVersionSelector';
import { IntegrationIcon } from '@/components/organisms/Integrations';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { NoIntegrationsEmptyState } from '@/modules/humanChat/components/NewMessageModal/NoIntegrationsEmptyState';
import { useContactLookup } from '@/modules/humanChat/hooks/useContactLookup';
import { useNewMessageForm } from '@/modules/humanChat/hooks/useNewMessageForm';
import { useNewMessageIntegrations } from '@/modules/humanChat/hooks/useNewMessageIntegrations';
import { useNewMessageSend } from '@/modules/humanChat/hooks/useNewMessageSend';
import { useNewMessageTemplates } from '@/modules/humanChat/hooks/useNewMessageTemplates';
import { popModal } from '@/redux/modals/actions';
import { trackEvent } from '@/segment/segment';
import { IntegrationType } from '@/shared/types/integration';
import { EventName } from '@/shared/types/segment';
import { getConfigKey, getDisplayName } from '@/util/outboundIntegrations';
import { emailPattern } from '@/util/validation/patterns';

import { ContactLookupHint } from './ContactLookupHint';
import { MessageAuthoring } from './MessageAuthoring';
import { NewMessageFooter } from './NewMessageFooter';

interface NewMessageModalProps {
  initialTo?: string;
  initialIntegrationId?: string;
}

/** Route a pre-filled recipient to the correct form field based on format. */
const routeInitialTo = (initialTo?: string) => {
  if (!initialTo) {
    return {};
  }
  if (emailPattern.test(initialTo)) {
    return { initialToEmail: initialTo };
  }
  // Normalize IDD-prefixed numbers like "0030..." to E.164 ("+30...") so the
  // PhoneInput's country detection can pick up the right country.
  const stripped = initialTo.replace(/^00/, '+');
  const phone = stripped.startsWith('+') ? stripped : `+${stripped}`;
  return { initialToPhone: phone };
};

export const NewMessageModal = ({ initialTo, initialIntegrationId }: NewMessageModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [step, setStep] = useState<0 | 1>(0);
  const [integrationPopoverOpen, setIntegrationPopoverOpen] = useState(false);
  const [phoneBlurred, setPhoneBlurred] = useState(false);

  // Normalize initialTo so the form default and the lookup hook receive the same value
  const routedInitialTo = routeInitialTo(initialTo);

  const {
    form,
    resetForm,
    headerFields,
    bodyFields,
    footerFields,
    headerReplace,
    bodyReplace,
    footerReplace,
    onContentChange,
    onSubjectChange,
    onAiAgentChange,
  } = useNewMessageForm({ ...routedInitialTo, initialIntegrationId });

  const selectedAiAgent = useWatch({ control: form.control, name: 'aiAgent' });
  const watchedToPhone = useWatch({ control: form.control, name: 'toPhone' });
  const watchedToEmail = useWatch({ control: form.control, name: 'toEmail' });
  const watchedRecipientName = useWatch({ control: form.control, name: 'recipientName' });

  const {
    integrations,
    integrationsLoading,
    selectedIntegration,
    selectedIntegrationType,
    hasNoIntegrations,
    onIntegrationChange,
  } = useNewMessageIntegrations({ form, initialTo });

  const {
    lookup: lookupContact,
    reset: contactReset,
    result: contactResult,
    loading: contactLoading,
    lookupComplete: contactLookupComplete,
    lookedUpValue: contactLookedUpValue,
  } = useContactLookup(
    selectedIntegrationType,
    routedInitialTo.initialToPhone ?? routedInitialTo.initialToEmail
  );

  const {
    templates,
    isTemplatesLoading,
    selectedTemplate,
    headerVariablesCount,
    bodyVariablesCount,
    footerVariablesCount,
    onTemplateChange,
  } = useNewMessageTemplates({
    form,
    selectedIntegration,
    selectedIntegrationType,
    headerReplace,
    bodyReplace,
    footerReplace,
  });

  const { sendMessage, isSending } = useNewMessageSend({
    form,
    selectedIntegration,
    selectedIntegrationType,
    selectedTemplate,
    contactContext: contactResult,
  });

  const { control } = form;

  const currentRecipient =
    selectedIntegrationType === IntegrationType.EMAIL ? watchedToEmail : watchedToPhone;

  // Step 0 → Step 1: validate only the step-0 fields before advancing.
  // Also ensures contact lookup has completed for the current recipient — if it
  // hasn't (e.g. user never blurred, or lookup was reset), trigger a fresh lookup
  // and bail so the user can click Next again once the result is in.
  const onNext = async () => {
    const valid = await form.trigger(['integrationId', 'integrationType', 'toPhone', 'toEmail']);
    if (!valid) {
      return;
    }

    // Contact lookup must have completed for the current recipient before advancing
    if (currentRecipient && currentRecipient !== contactLookedUpValue) {
      lookupContact(currentRecipient);
      return;
    }
    if (contactLoading) {
      return;
    }

    trackEvent(EventName.ClickNewMessageNext, {
      integration_type: selectedIntegrationType ?? undefined,
    });

    // Auto-select first approved WhatsApp template so the dialog opens at its final width
    const firstApproved = templates.find((t) => t.status === 'APPROVED');
    if (
      selectedIntegrationType === IntegrationType.WHATSAPP &&
      firstApproved &&
      !form.getValues('templateId')
    ) {
      onTemplateChange(firstApproved.id);
    }

    setStep(1);
  };

  const onBack = () => setStep(0);

  const closeModal = () => {
    resetForm();
    dispatch(popModal());
  };

  const onCloseAttempt = () => {
    closeModal();
  };

  // Ref guard prevents double-submit.
  const isSendingRef = useRef(false);

  const executeSend = async () => {
    isSendingRef.current = true;
    try {
      const success = await sendMessage();
      if (success) {
        closeModal();
      }
    } finally {
      isSendingRef.current = false;
    }
  };

  const onSend = async () => {
    if (isSendingRef.current) {
      return;
    }
    if (
      selectedIntegrationType === IntegrationType.WHATSAPP &&
      selectedTemplate !== null &&
      selectedTemplate.status !== 'APPROVED'
    ) {
      form.setError('templateId', {
        type: 'manual',
        message: 'conversation.new_message.validation.template_not_approved',
      });
      return;
    }
    const valid = await form.trigger();
    if (!valid) {
      return;
    }
    await executeSend();
  };

  const isNewContact =
    contactLookupComplete &&
    !contactResult &&
    !contactLoading &&
    currentRecipient === contactLookedUpValue;

  const getIntegrationDisplayName = (integration: typeof selectedIntegration) => {
    if (!integration) {
      return '';
    }
    const configKey = getConfigKey(integration.type as IntegrationType);
    if (!configKey) {
      return integration.name;
    }
    const senderIdentifier = (integration.config as Record<string, string>)?.[configKey];
    return `${getDisplayName(integration.type as IntegrationType)} ${senderIdentifier || integration.name}`;
  };

  const dialogMaxWidth = step === 1 ? 'sm:max-w-screen-md' : 'sm:max-w-xl';

  return (
    <>
      <Dialog open={true} onOpenChange={onCloseAttempt}>
        <DialogContent className={cn('max-h-[90vh] overflow-y-auto', dialogMaxWidth)}>
          <Form {...form}>
            {step === 0 && (
              <>
                <DialogHeader>
                  <DialogTitle>{t('conversation.new_message.title')}</DialogTitle>
                  <DialogDescription>
                    {t('conversation.new_message.subtitle_step0')}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-4 px-1">
                  {hasNoIntegrations ? (
                    <NoIntegrationsEmptyState onClose={closeModal} />
                  ) : (
                    <>
                      <FormField
                        control={control}
                        name="integrationId"
                        render={({ fieldState }) => (
                          <FormItem>
                            <FormLabel>{t('conversation.new_message.send_from')}</FormLabel>
                            <Popover
                              open={integrationPopoverOpen}
                              onOpenChange={setIntegrationPopoverOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline-neutral"
                                  role="combobox"
                                  aria-expanded={integrationPopoverOpen}
                                  disabled={integrationsLoading}
                                  className="h-10 w-full justify-between hover:bg-transparent"
                                >
                                  {integrationsLoading && <Skeleton className="h-4 w-40" />}
                                  {!integrationsLoading && selectedIntegration && (
                                    <div className="flex items-center gap-2 font-normal text-foreground">
                                      <IntegrationIcon
                                        type={selectedIntegration.type as IntegrationType}
                                        size={20}
                                      />
                                      <span>{getIntegrationDisplayName(selectedIntegration)}</span>
                                    </div>
                                  )}
                                  {!integrationsLoading && !selectedIntegration && (
                                    <span className="font-normal text-muted-foreground">
                                      {t('conversation.new_message.send_from_placeholder')}
                                    </span>
                                  )}
                                  <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                className="z-1300 w-(--radix-popover-trigger-width) p-0"
                                align="start"
                              >
                                <Command>
                                  <CommandInput placeholder={t('common.search')} />
                                  <CommandList>
                                    <CommandEmpty>
                                      {integrationsLoading
                                        ? t('common.loading')
                                        : t('common.no_integrations')}
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {integrations.map((integration) => (
                                        <CommandItem
                                          key={integration.integration_id}
                                          value={getIntegrationDisplayName(
                                            integration as typeof selectedIntegration
                                          )}
                                          onSelect={() => {
                                            trackEvent(EventName.SelectNewMessageIntegration, {
                                              integration_type: integration.type,
                                            });
                                            onIntegrationChange(integration.integration_id || '');
                                            setPhoneBlurred(false);
                                            setIntegrationPopoverOpen(false);
                                          }}
                                        >
                                          <div className="flex flex-1 items-center gap-2">
                                            <IntegrationIcon
                                              type={integration.type as IntegrationType}
                                              size={20}
                                            />
                                            <span>
                                              {getIntegrationDisplayName(
                                                integration as typeof selectedIntegration
                                              )}
                                            </span>
                                          </div>
                                          <CheckIcon
                                            className={cn(
                                              'ml-auto h-4 w-4',
                                              selectedIntegration?.integration_id ===
                                                integration.integration_id
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                            )}
                                          />
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                            {fieldState.error?.message && (
                              <p className="text-sm text-warning">{t(fieldState.error.message)}</p>
                            )}
                          </FormItem>
                        )}
                      />

                      {selectedIntegrationType === IntegrationType.EMAIL ? (
                        <FormField
                          control={control}
                          name="toEmail"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>{t('conversation.new_message.to')}</FormLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder={t('conversation.new_message.to_placeholder_email')}
                                  {...field}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    contactReset();
                                    form.setValue('recipientName', '');
                                  }}
                                  onBlur={(e) => {
                                    field.onBlur();
                                    if (e.target.value !== contactLookedUpValue) {
                                      lookupContact(e.target.value);
                                    }
                                  }}
                                />
                              </FormControl>
                              <div className="min-h-5">
                                {fieldState.error?.message ? (
                                  <p className="text-sm text-warning">
                                    {t(fieldState.error.message)}
                                  </p>
                                ) : field.value === contactLookedUpValue ? (
                                  <ContactLookupHint
                                    result={contactResult}
                                    loading={contactLoading}
                                    queried={contactLookupComplete}
                                  />
                                ) : (
                                  <p className="text-sm text-muted-foreground">&nbsp;</p>
                                )}
                              </div>
                            </FormItem>
                          )}
                        />
                      ) : (
                        <FormField
                          control={control}
                          name="toPhone"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>{t('conversation.new_message.to')}</FormLabel>
                              <FormControl>
                                <PhoneInput
                                  value={field.value}
                                  onChange={(value) => {
                                    field.onChange(value);
                                    contactReset();
                                    form.setValue('recipientName', '');
                                    setPhoneBlurred(false);
                                  }}
                                  onBlur={() => {
                                    field.onBlur();
                                    setPhoneBlurred(true);
                                    if (field.value !== contactLookedUpValue) {
                                      lookupContact(field.value);
                                    }
                                  }}
                                  defaultCountry="US"
                                  placeholder={t('conversation.new_message.to_placeholder')}
                                />
                              </FormControl>
                              <div className="min-h-5">
                                {phoneBlurred && fieldState.error?.message ? (
                                  <p className="text-sm text-warning">
                                    {t(fieldState.error.message)}
                                  </p>
                                ) : field.value === contactLookedUpValue ? (
                                  <ContactLookupHint
                                    result={contactResult}
                                    loading={contactLoading}
                                    queried={contactLookupComplete}
                                  />
                                ) : (
                                  <p className="text-sm text-muted-foreground">&nbsp;</p>
                                )}
                              </div>
                            </FormItem>
                          )}
                        />
                      )}

                      {isNewContact && (
                        <FormField
                          control={control}
                          name="recipientName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('conversation.new_message.customer_name')}</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder={t(
                                    'conversation.new_message.customer_name_placeholder'
                                  )}
                                  {...field}
                                  autoComplete="off"
                                />
                              </FormControl>
                              <FormDescription>
                                {t('conversation.new_message.customer_name_helper')}
                              </FormDescription>
                            </FormItem>
                          )}
                        />
                      )}

                      <FormField
                        control={control}
                        name="aiAgent"
                        render={() => (
                          <FormItem>
                            <FormLabel>{t('conversation.new_message.ai_agent')}</FormLabel>
                            <BrainVersionSelector
                              onSelect={onAiAgentChange}
                              initialBrainId={selectedAiAgent?.brain_parent_id}
                              initialVersion={selectedAiAgent?.brain_version}
                              contentClassName="z-1300"
                              placeholder={t('common.none')}
                            />
                            <FormDescription>
                              {t('conversation.new_message.ai_agent_helper')}
                            </FormDescription>
                          </FormItem>
                        )}
                      />
                    </>
                  )}
                </div>
              </>
            )}

            {step === 1 && selectedIntegrationType && (
              <>
                <DialogHeader>
                  <DialogTitle>
                    {t('conversation.new_message.title_step1', {
                      0:
                        contactResult?.userName ||
                        watchedRecipientName?.trim() ||
                        (selectedIntegrationType === IntegrationType.EMAIL
                          ? watchedToEmail
                          : watchedToPhone),
                    })}
                  </DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-4 overflow-y-auto px-1 pb-1">
                  <MessageAuthoring
                    selectedIntegrationType={selectedIntegrationType}
                    templates={templates}
                    isTemplatesLoading={isTemplatesLoading}
                    selectedTemplate={selectedTemplate}
                    headerFields={headerFields}
                    bodyFields={bodyFields}
                    footerFields={footerFields}
                    headerVariablesCount={headerVariablesCount}
                    bodyVariablesCount={bodyVariablesCount}
                    footerVariablesCount={footerVariablesCount}
                    onTemplateChange={onTemplateChange}
                    onContentChange={onContentChange}
                    onSubjectChange={onSubjectChange}
                  />
                </div>
              </>
            )}
          </Form>
          {!hasNoIntegrations && (
            <NewMessageFooter
              isSending={isSending}
              step={step}
              selectedIntegrationType={selectedIntegrationType}
              isNextLoading={contactLoading}
              isNewContact={isNewContact}
              onNext={onNext}
              onBack={onBack}
              onSend={onSend}
              onCloseAttempt={onCloseAttempt}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
